import React, { useEffect, useState } from 'react';
import { matrimonyAPI } from '../utils/api';

/**
 * FormField - Renders a single form field styled with Vivah Nepal design system
 */
function FormField({ field, value, onChange }) {
  const isFullWidth = field.fullWidth || field.type === 'textarea';

  return (
    <div style={{
      gridColumn: isFullWidth ? '1 / -1' : 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.4rem'
    }}>
      <label style={{
        fontSize: '0.78rem',
        fontWeight: 700,
        color: '#1C1014',
        textTransform: 'uppercase',
        letterSpacing: '0.04em'
      }}>
        {field.label}
        {field.required && <span style={{ color: '#9E1B32', marginLeft: '0.2rem' }}>*</span>}
      </label>

      {field.type === 'text' && (
        <input
          type="text"
          name={field.name}
          id={field.name}
          autoComplete={field.name}
          className="form-input"
          value={value}
          onChange={onChange}
          placeholder={field.placeholder || ''}
        />
      )}

      {field.type === 'email' && (
        <input
          type="email"
          name={field.name}
          id={field.name}
          autoComplete="email"
          className="form-input"
          value={value}
          onChange={onChange}
          placeholder={field.placeholder || ''}
        />
      )}

      {field.type === 'tel' && (
        <input
          type="tel"
          name={field.name}
          id={field.name}
          autoComplete="tel"
          className="form-input"
          value={value}
          onChange={onChange}
          placeholder={field.placeholder || ''}
        />
      )}

      {field.type === 'number' && (
        <input
          type="number"
          name={field.name}
          id={field.name}
          className="form-input"
          value={value}
          onChange={onChange}
          placeholder={field.placeholder || ''}
        />
      )}

      {field.type === 'date' && (
        <input
          type="date"
          name={field.name}
          id={field.name}
          className="form-input"
          value={value}
          onChange={onChange}
        />
      )}

      {field.type === 'time' && (
        <input
          type="time"
          name={field.name}
          id={field.name}
          className="form-input"
          value={value}
          onChange={onChange}
        />
      )}

      {field.type === 'select' && (
        <select
          name={field.name}
          id={field.name}
          className="form-input"
          value={value}
          onChange={onChange}
        >
          <option value="">-- Select {field.label} --</option>
          {field.options && field.options.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}

      {field.type === 'textarea' && (
        <textarea
          name={field.name}
          id={field.name}
          className="form-input"
          rows={3}
          value={value}
          onChange={onChange}
          placeholder={field.placeholder || ''}
          style={{ resize: 'vertical' }}
        />
      )}
    </div>
  );
}

/**
 * DynamicFormSection - Renders a form section based on backend configuration
 */
function DynamicFormSection({ sectionConfig, form, upd }) {
  if (!sectionConfig || !sectionConfig.fields) return null;

  // Visible fields calculation
  const visibleFields = sectionConfig.fields.filter(field => {
    if (!field.conditional) return true;
    const conditionalField = field.conditional;
    const conditionalValue = form[conditionalField.field];
    // Show if condition matches or if user has filled a value in the field
    return conditionalValue === conditionalField.value || !!form[field.name];
  });

  return (
    <div className="card" style={{ marginBottom: '1.8rem', border: '1.5px solid #D4AF37' }}>
      <div style={{
        fontFamily: 'var(--serif)',
        fontSize: '1.35rem',
        fontWeight: 700,
        color: '#9E1B32',
        marginBottom: '1.2rem',
        paddingBottom: '0.6rem',
        borderBottom: '1px solid #E8DFD1',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <span>{sectionConfig.icon}</span> {sectionConfig.title}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1.2rem'
      }}>
        {visibleFields.map(field => (
          <FormField
            key={field.name}
            field={field}
            value={form[field.name] || ''}
            onChange={upd(field.name)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * DynamicForm - Main component that loads form config from backend and renders sections
 */
export function DynamicForm({ form, upd, loading = false }) {
  const [formConfig, setFormConfig] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    matrimonyAPI.getProfileFormConfig()
      .then(config => {
        setFormConfig(config);
        setError(null);
      })
      .catch(err => {
        fetch('/api/matrimony/profile-form-config/')
          .then(r => r.json())
          .then(config => {
            setFormConfig(config);
            setError(null);
          })
          .catch(() => {
            const errorMsg = err?.detail || err?.message || 'Could not load form configuration. Please refresh the page.';
            setError(errorMsg);
          });
      });
  }, []);

  if (error) {
    return (
      <div style={{
        background: '#FEECEC',
        border: '1px solid rgba(158,27,50,0.3)',
        color: '#9E1B32',
        padding: '1rem 1.2rem',
        borderRadius: '14px',
        marginBottom: '1.5rem',
        fontSize: '0.88rem',
        fontWeight: 700
      }}>
        ⚠️ {error}
      </div>
    );
  }

  if (!formConfig) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: '#9E1B32', fontWeight: 700 }}>
        ⏳ Loading form fields...
      </div>
    );
  }

  return (
    <>
      {Object.entries(formConfig).map(([sectionKey, sectionConfig]) => (
        <DynamicFormSection
          key={sectionKey}
          sectionConfig={sectionConfig}
          form={form}
          upd={upd}
        />
      ))}
    </>
  );
}

export default DynamicForm;
